import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

// Calendar page preferences
interface CalendarPreferences {
  viewMode: "month" | "agenda";
  viewModeSetByUser: boolean; // Track if user manually changed view (don't auto-default)
  year: number;
  month: number;
  weekStart: Date;
}

// Reports page preferences
interface ReportsPreferences {
  selectedPersonnelId: string | null;
}

// Competencies page preferences
type CompetenciesViewMode = "list" | "search" | "advanced";

interface CompetenciesPreferences {
  viewMode: CompetenciesViewMode;
  selectedPersonId: string | null;
  competencySearch: string;
}

// Competencies search page preferences
interface CompetenciesSearchPreferences {
  selectedCompetencyType: string | null;
  selectedCompetencyTypes: string[];
}

// Advanced competencies search page preferences
interface QueryGroup {
  id: string;
  competencies: string[];
  internalOperator: "and" | "or";
}

interface QueryState {
  groups: QueryGroup[];
  connectors: ("and" | "or")[];
}

interface CompetenciesSearchAdvPreferences {
  query: QueryState;
}

// Travel page preferences
interface TravelPreferences {
  selectedPersonId: string | null;
}

// Settings preferences (isSupervisor is now computed from DataContext based on supervised data)
interface SettingsPreferences {
  // Placeholder for future settings preferences
}

interface ViewPreferencesContextValue {
  // Settings
  settingsPrefs: SettingsPreferences;
  // Reports
  reportsPrefs: ReportsPreferences;
  setReportsSelectedPersonnelId: (id: string | null) => void;
  
  // Calendar
  calendarPrefs: CalendarPreferences;
  setCalendarViewMode: (mode: "month" | "agenda") => void;
  setCalendarViewModeDefault: (mode: "month" | "agenda") => void; // Only sets if user hasn't manually changed
  setCalendarMonthYear: (year: number, month: number) => void;
  setCalendarWeekStart: (date: Date) => void;
  
  // Competencies
  competenciesPrefs: CompetenciesPreferences;
  setCompetenciesViewMode: (mode: CompetenciesViewMode) => void;
  setCompetenciesSelectedPerson: (id: string | null) => void;
  setCompetenciesSearch: (search: string) => void;
  
  // Competencies Search
  competenciesSearchPrefs: CompetenciesSearchPreferences;
  setCompetenciesSearchSelectedType: (type: string | null) => void;
  setCompetenciesSearchSelectedTypes: (types: string[]) => void;
  
  // Advanced Competencies Search
  competenciesSearchAdvPrefs: CompetenciesSearchAdvPreferences;
  setCompetenciesSearchAdvQuery: (query: QueryState) => void;
  
  // Travel
  travelPrefs: TravelPreferences;
  setTravelSelectedPerson: (id: string | null) => void;
}

// Helper to get Monday of the week
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

const now = new Date();

const defaultCalendarPrefs: CalendarPreferences = {
  viewMode: "agenda",
  viewModeSetByUser: false,
  year: now.getFullYear(),
  month: now.getMonth(),
  weekStart: getWeekStart(now)
};

const defaultCompetenciesPrefs: CompetenciesPreferences = {
  viewMode: "list",
  selectedPersonId: null,
  competencySearch: ""
};

const defaultCompetenciesSearchPrefs: CompetenciesSearchPreferences = {
  selectedCompetencyType: null,
  selectedCompetencyTypes: []
};

const defaultCompetenciesSearchAdvPrefs: CompetenciesSearchAdvPreferences = {
  query: {
    groups: [{ id: generateId(), competencies: [], internalOperator: "or" }],
    connectors: []
  }
};

const defaultTravelPrefs: TravelPreferences = {
  selectedPersonId: null
};

const defaultSettingsPrefs: SettingsPreferences = {
  // Placeholder for future settings
};

const ViewPreferencesContext = createContext<ViewPreferencesContextValue | undefined>(undefined);

export function ViewPreferencesProvider({ children }: { children: ReactNode }): JSX.Element {
  // Reports state - selectedPersonnelId is set when supervisor selects a different person to view
  const [reportsPrefs, setReportsPrefs] = useState<ReportsPreferences>({
    selectedPersonnelId: null
  });
  
  // Calendar state
  const [calendarPrefs, setCalendarPrefs] = useState<CalendarPreferences>(defaultCalendarPrefs);
  
  // Competencies state
  const [competenciesPrefs, setCompetenciesPrefs] = useState<CompetenciesPreferences>(defaultCompetenciesPrefs);
  
  // Competencies Search state
  const [competenciesSearchPrefs, setCompetenciesSearchPrefs] = useState<CompetenciesSearchPreferences>(defaultCompetenciesSearchPrefs);
  
  // Advanced Competencies Search state
  const [competenciesSearchAdvPrefs, setCompetenciesSearchAdvPrefs] = useState<CompetenciesSearchAdvPreferences>(defaultCompetenciesSearchAdvPrefs);
  
  // Travel state
  const [travelPrefs, setTravelPrefs] = useState<TravelPreferences>(defaultTravelPrefs);
  
  // Settings state
  const [settingsPrefs] = useState<SettingsPreferences>(defaultSettingsPrefs);

  // Reports setters
  const setReportsSelectedPersonnelId = useCallback((id: string | null) => {
    setReportsPrefs(prev => ({ ...prev, selectedPersonnelId: id }));
  }, []);

  // Calendar setters
  const setCalendarViewMode = useCallback((mode: "month" | "agenda") => {
    // User-initiated change - mark as set by user so auto-default won't override
    setCalendarPrefs(prev => ({ ...prev, viewMode: mode, viewModeSetByUser: true }));
  }, []);

  const setCalendarViewModeDefault = useCallback((mode: "month" | "agenda") => {
    // Auto-default - only set if user hasn't manually changed the view
    setCalendarPrefs(prev => {
      if (prev.viewModeSetByUser) return prev; // Don't override user's choice
      return { ...prev, viewMode: mode };
    });
  }, []);

  const setCalendarMonthYear = useCallback((year: number, month: number) => {
    setCalendarPrefs(prev => ({ ...prev, year, month }));
  }, []);

  const setCalendarWeekStart = useCallback((date: Date) => {
    setCalendarPrefs(prev => ({ ...prev, weekStart: date }));
  }, []);

  // Competencies setters
  const setCompetenciesViewMode = useCallback((mode: CompetenciesViewMode) => {
    setCompetenciesPrefs(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const setCompetenciesSelectedPerson = useCallback((id: string | null) => {
    setCompetenciesPrefs(prev => ({ ...prev, selectedPersonId: id }));
  }, []);

  const setCompetenciesSearch = useCallback((search: string) => {
    setCompetenciesPrefs(prev => ({ ...prev, competencySearch: search }));
  }, []);

  // Competencies Search setters
  const setCompetenciesSearchSelectedType = useCallback((type: string | null) => {
    setCompetenciesSearchPrefs(prev => ({ ...prev, selectedCompetencyType: type }));
  }, []);

  const setCompetenciesSearchSelectedTypes = useCallback((types: string[]) => {
    setCompetenciesSearchPrefs(prev => ({ ...prev, selectedCompetencyTypes: types }));
  }, []);

  // Advanced Competencies Search setters
  const setCompetenciesSearchAdvQuery = useCallback((query: QueryState) => {
    setCompetenciesSearchAdvPrefs({ query });
  }, []);

  // Travel setters
  const setTravelSelectedPerson = useCallback((id: string | null) => {
    setTravelPrefs(prev => ({ ...prev, selectedPersonId: id }));
  }, []);

  return (
    <ViewPreferencesContext.Provider
      value={{
        settingsPrefs,
        reportsPrefs,
        setReportsSelectedPersonnelId,
        calendarPrefs,
        setCalendarViewMode,
        setCalendarViewModeDefault,
        setCalendarMonthYear,
        setCalendarWeekStart,
        competenciesPrefs,
        setCompetenciesViewMode,
        setCompetenciesSelectedPerson,
        setCompetenciesSearch,
        competenciesSearchPrefs,
        setCompetenciesSearchSelectedType,
        setCompetenciesSearchSelectedTypes,
        competenciesSearchAdvPrefs,
        setCompetenciesSearchAdvQuery,
        travelPrefs,
        setTravelSelectedPerson
      }}
    >
      {children}
    </ViewPreferencesContext.Provider>
  );
}

export function useViewPreferences(): ViewPreferencesContextValue {
  const ctx = useContext(ViewPreferencesContext);
  if (!ctx) {
    throw new Error("useViewPreferences must be used inside ViewPreferencesProvider");
  }
  return ctx;
}

export type { CompetenciesViewMode };
